import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState, useMemo, useCallback } from 'react';
import {
  Plus, Edit2, Trash2, Search, Layers, Ruler, ChevronDown,
  Car, Home as HomeIcon, X as XIcon,
} from 'lucide-react';
import {
  fetchAssetCategories, createAssetCategory, updateAssetCategory, deleteAssetCategory,
  fetchQuantityUnits, createQuantityUnit, updateQuantityUnit, deleteQuantityUnit,
} from './hooks/hooks';
import {
  Toast, LoadingState, ErrorState, EmptyState, Pagination,
  ConfirmModal, URITHI,
} from './ui';

const inputCls = 'w-full px-3.5 py-2.5 border border-gray-200 text-sm rounded-xl focus:outline-none focus:border-[#001f3f] focus:ring-2 focus:ring-[#001f3f]/10 transition-all bg-white placeholder:text-gray-400';

const Field = ({ label, error, children, hint }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-bold text-[#001f3f] uppercase tracking-wide block">{label}</label>
    {children}
    {hint && !error && <p className="text-[11px] text-gray-500">{hint}</p>}
    {error && <p className="text-[11px] text-red-500 font-medium">{error}</p>}
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// Modal form (used for both create & edit, both categories & units)
// ═══════════════════════════════════════════════════════════════════════════
const FormModal = ({ open, title, onClose, onSubmit, fields, values, errors, onFieldChange, isLoading, submitLabel }) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h3 className="text-base font-bold text-[#001f3f] uppercase tracking-wide">{title}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-[#001f3f] hover:bg-gray-100 transition-colors">
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} className="p-5 space-y-4">
          {fields.map((f) => (
            <Field key={f.name} label={f.label} error={errors[f.name]} hint={f.hint}>
              {f.type === 'select' ? (
                <div className="relative">
                  <select
                    value={values[f.name] || ''}
                    onChange={(e) => onFieldChange(f.name, e.target.value)}
                    className={`${inputCls} appearance-none pr-9`}
                  >
                    {!values[f.name] && <option value="">{f.placeholder || 'Select…'}</option>}
                    {f.options.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              ) : f.type === 'textarea' ? (
                <textarea
                  value={values[f.name] || ''}
                  onChange={(e) => onFieldChange(f.name, e.target.value)}
                  rows={3}
                  className={`${inputCls} resize-y`}
                  placeholder={f.placeholder}
                />
              ) : (
                <input
                  type={f.type || 'text'}
                  value={values[f.name] || ''}
                  onChange={(e) => onFieldChange(f.name, e.target.value)}
                  className={inputCls}
                  placeholder={f.placeholder}
                />
              )}
            </Field>
          ))}

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={isLoading} className="flex-1 px-4 py-2.5 rounded-xl bg-[#001f3f] hover:bg-[#0a3260] text-white text-sm font-semibold transition-colors disabled:opacity-50">
              {isLoading ? 'Saving…' : (submitLabel || 'Save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Asset Categories panel
// ═══════════════════════════════════════════════════════════════════════════
function CategoriesPanel() {
  const queryClient = useQueryClient();
  const { data: categories, isLoading, isError, refetch } = useQuery({
    queryKey: ['assetCategories'],
    queryFn: () => fetchAssetCategories(),
  });

  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [modal, setModal] = useState({ open: false, mode: 'create', item: null });
  const [form, setForm] = useState({ name: '', asset_type: 'Mobile', icon: '', description: '' });
  const [errors, setErrors] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, name: '' });
  const [toast, setToast] = useState({ open: false, message: '', severity: '' });

  const filtered = useMemo(() => {
    if (!Array.isArray(categories)) return [];
    return categories.filter((c) => {
      if (search && !c.name?.toLowerCase().includes(search.toLowerCase())) return false;
      if (filterType !== 'all' && c.asset_type !== filterType) return false;
      return true;
    });
  }, [categories, search, filterType]);

  const validate = () => {
    const e = {};
    if (!form.name?.trim()) e.name = 'Name is required';
    if (!form.asset_type) e.asset_type = 'Asset type is required';
    return e;
  };

  const createMutation = useMutation({
    mutationFn: createAssetCategory,
    onSuccess: () => {
      queryClient.invalidateQueries(['assetCategories']);
      setModal({ open: false, mode: 'create', item: null });
      setForm({ name: '', asset_type: 'Mobile', icon: '', description: '' });
      setToast({ open: true, message: 'Category created', severity: 'success' });
    },
    onError: (err) => setToast({ open: true, message: err?.response?.data?.name?.[0] || 'Failed to create category', severity: 'error' }),
  });

  const updateMutation = useMutation({
    mutationFn: updateAssetCategory,
    onSuccess: () => {
      queryClient.invalidateQueries(['assetCategories']);
      setModal({ open: false, mode: 'create', item: null });
      setToast({ open: true, message: 'Category updated', severity: 'success' });
    },
    onError: () => setToast({ open: true, message: 'Failed to update category', severity: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteAssetCategory,
    onSuccess: () => {
      queryClient.invalidateQueries(['assetCategories']);
      setDeleteConfirm({ open: false, id: null, name: '' });
      setToast({ open: true, message: 'Category deactivated', severity: 'success' });
    },
    onError: () => setToast({ open: true, message: 'Failed to deactivate category', severity: 'error' }),
  });

  const handleOpenCreate = () => {
    setForm({ name: '', asset_type: 'Mobile', icon: '', description: '' });
    setErrors({});
    setModal({ open: true, mode: 'create', item: null });
  };

  const handleOpenEdit = (item) => {
    setForm({
      name: item.name || '',
      asset_type: item.asset_type || 'Mobile',
      icon: item.icon || '',
      description: item.description || '',
    });
    setErrors({});
    setModal({ open: true, mode: 'edit', item });
  };

  const handleSubmit = () => {
    const errs = validate();
    if (Object.keys(errs).length) return setErrors(errs);
    if (modal.mode === 'edit') {
      updateMutation.mutate({ id: modal.item.id, data: form });
    } else {
      createMutation.mutate(form);
    }
  };

  if (isLoading) return <LoadingState message="Loading categories…" />;
  if (isError) return <ErrorState onRetry={refetch} />;

  return (
    <div>
      <Toast {...toast} onClose={() => setToast({ open: false, message: '', severity: '' })} />

      <ConfirmModal
        open={deleteConfirm.open}
        title="Deactivate Category"
        message={`Deactivate "${deleteConfirm.name}"? Existing assets in this category keep working (PROTECT relation), but no new assets can be created under it.`}
        confirmLabel="Yes, Deactivate"
        danger
        loading={deleteMutation.isLoading}
        onCancel={() => setDeleteConfirm({ open: false, id: null, name: '' })}
        onConfirm={() => deleteMutation.mutate(deleteConfirm.id)}
      />

      <FormModal
        open={modal.open}
        title={modal.mode === 'edit' ? 'Edit Category' : 'New Category'}
        onClose={() => setModal({ open: false, mode: 'create', item: null })}
        onSubmit={handleSubmit}
        values={form}
        errors={errors}
        onFieldChange={(name, value) => {
          setForm((p) => ({ ...p, [name]: value }));
          if (errors[name]) setErrors((p) => { const n = { ...p }; delete n[name]; return n; });
        }}
        isLoading={createMutation.isLoading || updateMutation.isLoading}
        submitLabel={modal.mode === 'edit' ? 'Update Category' : 'Create Category'}
        fields={[
          { name: 'name', label: 'Category Name', placeholder: 'e.g. Cars, Houses, Phones' },
          {
            name: 'asset_type',
            label: 'Asset Type',
            type: 'select',
            options: [
              { value: 'Mobile', label: 'Mobile (cars, phones, etc.)' },
              { value: 'Immobile', label: 'Immobile (houses, land, etc.)' },
            ],
          },
          { name: 'icon', label: 'Icon Name (optional)', placeholder: 'e.g. car, home-city-outline', hint: 'MaterialCommunityIcons name used in the mobile app' },
          { name: 'description', label: 'Description (optional)', type: 'textarea', placeholder: 'Brief description of what fits in this category' },
        ]}
      />

      {/* Toolbar */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={handleOpenCreate}
            className="inline-flex items-center gap-1.5 bg-[#001f3f] hover:bg-[#0a3260] text-white px-3.5 py-2 rounded-full text-xs font-bold transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Category
          </button>

          <div className="flex bg-gray-50 rounded-xl border border-gray-200 p-0.5">
            {['all', 'Mobile', 'Immobile'].map((t) => (
              <button
                key={t}
                onClick={() => setFilterType(t)}
                className={`px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wide transition-colors ${
                  filterType === t ? 'bg-[#001f3f] text-white' : 'text-gray-600 hover:text-[#001f3f]'
                }`}
              >
                {t === 'all' ? 'All' : t}
              </button>
            ))}
          </div>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search categories…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-3 py-2 text-xs border border-gray-200 rounded-xl focus:outline-none focus:border-[#001f3f] focus:ring-2 focus:ring-[#001f3f]/10 w-full sm:w-64 bg-gray-50"
          />
        </div>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <EmptyState
          title="No Categories Yet"
          message="Create the first category to start tagging assets dynamically."
          action={
            <button onClick={handleOpenCreate} className="inline-flex items-center gap-1.5 bg-[#001f3f] hover:bg-[#0a3260] text-white px-4 py-2 rounded-full text-xs font-bold transition-colors">
              <Plus className="w-3.5 h-3.5" />
              Add First Category
            </button>
          }
        />
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  {['Category', 'Type', 'Icon', 'Assets', 'Description', 'Actions'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-[10px] font-bold text-gray-600 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((cat) => (
                  <tr key={cat.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${cat.asset_type === 'Mobile' ? 'bg-emerald-50' : 'bg-blue-50'}`}>
                          {cat.asset_type === 'Mobile' ? <Car className="w-4 h-4 text-emerald-600" /> : <HomeIcon className="w-4 h-4 text-blue-600" />}
                        </div>
                        <p className="text-xs font-bold text-[#001f3f]">{cat.name}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                        cat.asset_type === 'Mobile' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-blue-50 text-blue-700 border-blue-200'
                      }`}>
                        {cat.asset_type}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <code className="text-[11px] text-gray-500 font-mono">{cat.icon || '—'}</code>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-bold text-[#001f3f]">{cat.asset_count ?? 0}</span>
                    </td>
                    <td className="px-4 py-3 text-[11px] text-gray-600 max-w-xs truncate">{cat.description || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => handleOpenEdit(cat)} className="p-2 rounded-lg text-[#001f3f] hover:bg-[#001f3f]/10 transition-colors">
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => setDeleteConfirm({ open: true, id: cat.id, name: cat.name })} className="p-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            page={page}
            rowsPerPage={rowsPerPage}
            total={filtered.length}
            onPageChange={setPage}
            onRowsPerPageChange={(r) => { setRowsPerPage(r); setPage(0); }}
          />
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Quantity Units panel
// ═══════════════════════════════════════════════════════════════════════════
function UnitsPanel() {
  const queryClient = useQueryClient();
  const { data: units, isLoading, isError, refetch } = useQuery({
    queryKey: ['quantityUnits'],
    queryFn: fetchQuantityUnits,
  });

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [modal, setModal] = useState({ open: false, mode: 'create', item: null });
  const [form, setForm] = useState({ name: '', symbol: '' });
  const [errors, setErrors] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, name: '' });
  const [toast, setToast] = useState({ open: false, message: '', severity: '' });

  const filtered = useMemo(() => {
    if (!Array.isArray(units)) return [];
    return units.filter((u) =>
      !search ||
      u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.symbol?.toLowerCase().includes(search.toLowerCase())
    );
  }, [units, search]);

  const validate = () => {
    const e = {};
    if (!form.name?.trim()) e.name = 'Unit name is required';
    return e;
  };

  const createMutation = useMutation({
    mutationFn: createQuantityUnit,
    onSuccess: () => {
      queryClient.invalidateQueries(['quantityUnits']);
      setModal({ open: false, mode: 'create', item: null });
      setForm({ name: '', symbol: '' });
      setToast({ open: true, message: 'Unit created', severity: 'success' });
    },
    onError: (err) => setToast({ open: true, message: err?.response?.data?.name?.[0] || 'Failed to create unit', severity: 'error' }),
  });

  const updateMutation = useMutation({
    mutationFn: updateQuantityUnit,
    onSuccess: () => {
      queryClient.invalidateQueries(['quantityUnits']);
      setModal({ open: false, mode: 'create', item: null });
      setToast({ open: true, message: 'Unit updated', severity: 'success' });
    },
    onError: () => setToast({ open: true, message: 'Failed to update unit', severity: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteQuantityUnit,
    onSuccess: () => {
      queryClient.invalidateQueries(['quantityUnits']);
      setDeleteConfirm({ open: false, id: null, name: '' });
      setToast({ open: true, message: 'Unit deactivated', severity: 'success' });
    },
    onError: () => setToast({ open: true, message: 'Failed to deactivate unit', severity: 'error' }),
  });

  const handleOpenCreate = () => {
    setForm({ name: '', symbol: '' });
    setErrors({});
    setModal({ open: true, mode: 'create', item: null });
  };

  const handleOpenEdit = (item) => {
    setForm({ name: item.name || '', symbol: item.symbol || '' });
    setErrors({});
    setModal({ open: true, mode: 'edit', item });
  };

  const handleSubmit = () => {
    const errs = validate();
    if (Object.keys(errs).length) return setErrors(errs);
    if (modal.mode === 'edit') {
      updateMutation.mutate({ id: modal.item.id, data: form });
    } else {
      createMutation.mutate(form);
    }
  };

  if (isLoading) return <LoadingState message="Loading units…" />;
  if (isError) return <ErrorState onRetry={refetch} />;

  return (
    <div>
      <Toast {...toast} onClose={() => setToast({ open: false, message: '', severity: '' })} />

      <ConfirmModal
        open={deleteConfirm.open}
        title="Deactivate Unit"
        message={`Deactivate "${deleteConfirm.name}"? Existing assets using this unit keep working.`}
        confirmLabel="Yes, Deactivate"
        danger
        loading={deleteMutation.isLoading}
        onCancel={() => setDeleteConfirm({ open: false, id: null, name: '' })}
        onConfirm={() => deleteMutation.mutate(deleteConfirm.id)}
      />

      <FormModal
        open={modal.open}
        title={modal.mode === 'edit' ? 'Edit Unit' : 'New Unit'}
        onClose={() => setModal({ open: false, mode: 'create', item: null })}
        onSubmit={handleSubmit}
        values={form}
        errors={errors}
        onFieldChange={(name, value) => {
          setForm((p) => ({ ...p, [name]: value }));
          if (errors[name]) setErrors((p) => { const n = { ...p }; delete n[name]; return n; });
        }}
        isLoading={createMutation.isLoading || updateMutation.isLoading}
        submitLabel={modal.mode === 'edit' ? 'Update Unit' : 'Create Unit'}
        fields={[
          { name: 'name', label: 'Unit Name', placeholder: 'e.g. piece, kilogram, acre' },
          { name: 'symbol', label: 'Symbol (optional)', placeholder: 'e.g. pcs, kg, ac', hint: 'Short abbreviation shown next to quantities' },
        ]}
      />

      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
        <button
          onClick={handleOpenCreate}
          className="inline-flex items-center gap-1.5 bg-[#001f3f] hover:bg-[#0a3260] text-white px-3.5 py-2 rounded-full text-xs font-bold transition-colors w-fit"
        >
          <Plus className="w-3.5 h-3.5" />
          Add Unit
        </button>

        <div className="relative">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search units…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-3 py-2 text-xs border border-gray-200 rounded-xl focus:outline-none focus:border-[#001f3f] focus:ring-2 focus:ring-[#001f3f]/10 w-full sm:w-64 bg-gray-50"
          />
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          title="No Units Yet"
          message="Add units of measurement like piece, kg, acre, etc."
          action={
            <button onClick={handleOpenCreate} className="inline-flex items-center gap-1.5 bg-[#001f3f] hover:bg-[#0a3260] text-white px-4 py-2 rounded-full text-xs font-bold transition-colors">
              <Plus className="w-3.5 h-3.5" />
              Add First Unit
            </button>
          }
        />
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  {['Name', 'Symbol', 'Status', 'Actions'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-[10px] font-bold text-gray-600 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((u) => (
                  <tr key={u.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-xl bg-[#C9982A]/10 flex items-center justify-center">
                          <Ruler className="w-4 h-4 text-[#C9982A]" />
                        </div>
                        <p className="text-xs font-bold text-[#001f3f] capitalize">{u.name}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <code className="text-[11px] text-gray-700 font-mono px-2 py-0.5 rounded bg-gray-100">{u.symbol || '—'}</code>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                        u.is_active !== false
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : 'bg-gray-50 text-gray-700 border-gray-200'
                      }`}>
                        {u.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => handleOpenEdit(u)} className="p-2 rounded-lg text-[#001f3f] hover:bg-[#001f3f]/10 transition-colors">
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => setDeleteConfirm({ open: true, id: u.id, name: u.name })} className="p-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            page={page}
            rowsPerPage={rowsPerPage}
            total={filtered.length}
            onPageChange={setPage}
            onRowsPerPageChange={(r) => { setRowsPerPage(r); setPage(0); }}
          />
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Container — sub-tabs for Categories vs Units
// ═══════════════════════════════════════════════════════════════════════════
function UrithiSettings() {
  const [subTab, setSubTab] = useState('categories');

  const tabs = [
    { id: 'categories', label: 'Asset Categories', icon: Layers, description: 'Mobile / Immobile category definitions used across the marketplace' },
    { id: 'units',      label: 'Quantity Units',   icon: Ruler,  description: 'Units of measurement: piece, kg, acre, hectare, etc.' },
  ];

  const active = tabs.find((t) => t.id === subTab);
  const ActiveIcon = active.icon;

  return (
    <div>
      {/* Sub-tab pills */}
      <div className="bg-white rounded-2xl border border-gray-100 mb-4 overflow-hidden">
        <div className="flex border-b border-gray-100">
          {tabs.map((t) => {
            const TabIcon = t.icon;
            const isActive = subTab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setSubTab(t.id)}
                className={`relative flex items-center gap-2 px-5 py-3.5 text-xs font-bold uppercase tracking-wide transition-colors ${
                  isActive ? 'text-[#001f3f] bg-white' : 'text-gray-500 hover:text-[#001f3f]'
                }`}
              >
                <TabIcon className="w-3.5 h-3.5" />
                {t.label}
                {isActive && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#001f3f]" />}
              </button>
            );
          })}
        </div>
        <div className="px-5 py-3 bg-gray-50/50">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: URITHI.navy + '18' }}>
              <ActiveIcon className="w-3.5 h-3.5 text-[#001f3f]" />
            </div>
            <p className="text-[11px] text-gray-600">{active.description}</p>
          </div>
        </div>
      </div>

      {subTab === 'categories' ? <CategoriesPanel /> : <UnitsPanel />}
    </div>
  );
}

export default UrithiSettings;